import React, { useState, useRef } from "react";
import {
  HiBeaker,
  HiChartBar,
  HiChatAlt2,
  HiSparkles,
  HiLightningBolt,
  HiPlay,
  HiOutlineCheck,
  HiChevronDown,
  HiStar,
  HiDocumentText,
  HiUserGroup,
  HiChevronRight,
  HiOutlineX,
  HiBriefcase,
  HiCubeTransparent,
  HiGlobeAlt,
  HiDocument,
  HiCursorClick,
  HiX,
  HiOutlineMail,
  HiCalendar,
  HiClipboardCheck,
} from "react-icons/hi";
import { Play, Pause, Link, Sparkles, CheckCircle, ArrowRight, Star, ActivityIcon } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
// import Header from "../components/Header";
import EnhancedHeader from "./EnhancedHeader";
import IframeResizer from "iframe-resizer-react";
import Footer from "../components/Footer";
import api from "./axiosConfig";
import { auth } from "../firebaseConfig";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import HowItWorks from "./HowItWorks";

const WhiteLabelSection = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  // State for modal visibility
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(false);

    // Get form data
    const form = e.target;
    const formData = new FormData(form);

    // Convert FormData to JSON object
    const formObject = {};
    formData.forEach((value, key) => {
      // Skip the FormSubmit configuration fields that start with underscore
      if (!key.startsWith("_")) {
        // Handle checkboxes (multiple values with same name)
        if (formObject[key]) {
          if (Array.isArray(formObject[key])) {
            formObject[key].push(value);
          } else {
            formObject[key] = [formObject[key], value];
          }
        } else {
          formObject[key] = value;
        }
      }
    });

    try {
      // Use the AJAX endpoint
      const response = await fetch("https://formsubmit.co/ajax/business@feedbackbyai.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formObject),
      });

      const result = await response.json();

      if (result.success === "true" || result.success === true) {
        // Show the thank you modal
        setSubmitting(false);
        setShowModal(true);

        // Reset form
        form.reset();
      } else {
        console.error("Form submission failed:", result);
        setSubmitting(false);
        setSubmitError(true);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitting(false);
      setSubmitError(true);
    }
  };

  // Close modal handler
  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50" id="white-label">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left Column: Content */}
            <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center">
              <motion.div
                variants={itemVariants}
                className="inline-flex items-center gap-2 bg-green-100 rounded-full px-4 py-1.5 text-sm text-green-700 font-semibold mb-5"
              >
                <HiBriefcase className="w-4 h-4" />
                For Agencies, Consultants & Accelerators
              </motion.div>

              <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                🚀 White-Label FeedbackbyAI for Your Clients
              </motion.h2>

              <motion.p variants={itemVariants} className="text-base sm:text-lg text-gray-600 mb-6">
                {/* Offer investor-ready business plans, lead generation & GTM content — all under your own brand, your own domain. Built for
                incubators, startup consultants, and B2B service providers. */}
                Offer all 4 tools — business plans, lead gen, TikToks, and research — under your own brand, domain, and pricing. Become a
                Partner.
              </motion.p>

              {/* <motion.p variants={itemVariants} className="text-sm text-purple-700 font-medium mb-6">
                We're currently inviting select partners to our private beta. Let us know a few details — and we'll get back to you with
                next steps.
              </motion.p> */}

              {/* Error message */}
              {submitError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
                >
                  <p className="font-medium">Unable to submit your request</p>
                  <p className="text-sm">Please try again or contact us directly at business@feedbackbyai.com</p>
                </motion.div>
              )}

              <motion.div variants={itemVariants}>
                <form method="POST" className="space-y-4" onSubmit={handleSubmit}>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        required
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        required
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Role
                      </label>
                      <input
                        type="text"
                        id="role"
                        name="role"
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="clientsMonthly" className="block text-sm font-medium text-gray-700 mb-1">
                      How many startups/clients do you serve monthly?
                    </label>
                    <select
                      id="clientsMonthly"
                      name="clientsMonthly"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    >
                      <option value="">Select an option</option>
                      <option value="1-5">1-5 clients</option>
                      <option value="6-10">6-10 clients</option>
                      <option value="11-20">11-20 clients</option>
                      <option value="21-50">21-50 clients</option>
                      <option value="51-100">51-100 clients</option>
                      <option value="100+">More than 100 clients</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      What would you use FeedbackbyAI for? (select all that apply)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="businessPlans"
                          name="useCases"
                          value="Business Plans"
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label htmlFor="businessPlans" className="ml-2 text-sm text-gray-700">
                          Business Plans
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="leadGen"
                          name="useCases"
                          value="Lead Generation"
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label htmlFor="leadGen" className="ml-2 text-sm text-gray-700">
                          Lead Gen
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="tiktokReels"
                          name="useCases"
                          value="TikTok/Reels Content"
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label htmlFor="tiktokReels" className="ml-2 text-sm text-gray-700">
                          TikTok/Reels
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="other"
                          name="useCases"
                          value="Other"
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label htmlFor="other" className="ml-2 text-sm text-gray-700">
                          Other
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
                      Anything else you'd like us to know?
                    </label>
                    <textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      rows="2"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full sm:w-auto px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors duration-300 flex items-center justify-center ${
                      submitting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {submitting ? (
                      <>
                        <span className="animate-spin h-4 w-4 mr-2 border-2 border-white rounded-full border-t-transparent"></span>
                        Submitting...
                      </>
                    ) : (
                      "Submit Interest"
                    )}
                  </button>
                </form>
              </motion.div>
            </div>

            {/* Right Column: Mockup */}
            <div className="relative hidden md:block bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[70%] max-w-md">
                {/* White Label Dashboard Mockup */}
                <div className="bg-white rounded-xl shadow-lg p-6 transform rotate-1 border border-purple-100 hover:shadow-xl transition-all duration-500">
                  {/* Browser-like header */}
                  <div className="h-8 flex items-center border-b border-gray-100 mb-4">
                    <div className="w-2 h-2 rounded-full bg-red-400 mr-2"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-400 mr-2"></div>
                    <div className="w-2 h-2 rounded-full bg-green-400 mr-2"></div>
                    <div className="flex-grow"></div>
                    <div className="bg-purple-100 px-2 py-1 rounded-md text-xs text-purple-700">YourBrand.com</div>
                  </div>

                  {/* Dashboard mockup */}
                  <div className="space-y-3">
                    <div className="h-8 bg-gradient-to-r from-purple-600 to-purple-800 rounded-md w-2/3 flex items-center px-3">
                      <span className="text-white text-xs font-medium">Client Dashboard</span>
                    </div>
                  </div>

                  {/* Feature tiles */}
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="h-20 bg-purple-50 rounded-md flex items-center justify-center p-2 border border-purple-100">
                      <div className="text-center">
                        <HiDocument className="h-6 w-6 text-purple-400 mx-auto mb-1" />
                        <span className="text-xs text-purple-700">Business Plans</span>
                      </div>
                    </div>
                    <div className="h-20 bg-green-50 rounded-md flex items-center justify-center p-2 border border-green-100">
                      <div className="text-center">
                        <HiUserGroup className="h-6 w-6 text-green-400 mx-auto mb-1" />
                        <span className="text-xs text-green-700">Lead Gen</span>
                      </div>
                    </div>
                    <div className="h-20 bg-blue-50 rounded-md flex items-center justify-center p-2 border border-blue-100">
                      <div className="text-center">
                        <HiOutlineCheck className="h-6 w-6 text-blue-400 mx-auto mb-1" />
                        <span className="text-xs text-blue-700">GTM Plans</span>
                      </div>
                    </div>
                    <div className="h-20 bg-orange-50 rounded-md flex items-center justify-center p-2 border border-orange-100">
                      <div className="text-center">
                        <HiCursorClick className="h-6 w-6 text-orange-400 mx-auto mb-1" />
                        <span className="text-xs text-orange-700">Social Videos</span>
                      </div>
                    </div>
                  </div>

                  {/* Client stats */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="text-xs text-gray-500 mb-2">Client Overview</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white p-2 rounded border border-gray-100">
                        <div className="text-xs text-gray-600">Active Plans</div>
                        <div className="text-lg font-semibold text-purple-700">15</div>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-100">
                        <div className="text-xs text-gray-600">New Leads</div>
                        <div className="text-lg font-semibold text-green-700">28</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Thank You Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={closeModal}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: "spring", damping: 20 }}
                className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative"
                onClick={(e) => e.stopPropagation()}
              >
                <button onClick={closeModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
                  <HiX className="w-5 h-5" />
                </button>

                <div className="text-center mb-6">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600">
                    Your request for white-label access has been received. We're excited to explore this partnership!
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Here's what happens next:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5 mr-3">
                        <HiOutlineMail className="w-4 h-4 text-purple-600" />
                      </div>
                      <p className="text-sm text-gray-600">
                        You'll receive a confirmation email with details about our white-label program.
                      </p>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5 mr-3">
                        <HiCalendar className="w-4 h-4 text-purple-600" />
                      </div>
                      <p className="text-sm text-gray-600">Our team will reach out within 48 hours to schedule a discovery call.</p>
                    </li>
                    {/* <li className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5 mr-3">
                        <HiClipboardCheck className="w-4 h-4 text-purple-600" />
                      </div>
                      <p className="text-sm text-gray-600">We'll create a personalized demo of your branded dashboard.</p>
                    </li> */}
                  </ul>
                </div>

                <button
                  onClick={closeModal}
                  className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors duration-300"
                >
                  Got it, thanks!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const VideoPlayer = ({ src }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  return (
    <div className="relative aspect-[9/16] bg-black rounded-2xl overflow-hidden shadow-xl ring-1 ring-gray-200">
      <video ref={videoRef} src={src} className="w-full h-full object-cover" loop playsInline />
      <button onClick={togglePlay} className="absolute bottom-3 right-3 bg-black/70 p-2 rounded-full shadow-md text-white hover:bg-black">
        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
      </button>
    </div>
  );
};

const handleSignUp = () => {
  window.location.href = "/signup";
};

const handleGoogleSignUp = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    const token = result.user.accessToken;
    const response = await api.post("/google-login/", { token });
    if (response.status === 200) {
      localStorage.setItem("access", response.data.access);
      window.location.href = "/dashboard/";
    }
  } catch (error) {
    console.error("Error with Google Sign Up:", error.message);
  }
};

// Hero Components
const FeaturePill = ({ icon: Icon, text }) => (
  <div className="flex items-center bg-green-50 hover:bg-green-100 px-4 py-2 rounded-full transition-colors duration-300">
    <Icon className="h-5 w-5 text-green-600 mr-2" />
    <span className="text-sm font-medium text-gray-900">{text}</span>
  </div>
);

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const Feature = ({ icon: Icon, text }) => (
  <div className="bg-green-50 hover:bg-green-100 px-4 sm:px-6 py-3 rounded-full transition-all duration-300 w-full sm:w-auto">
    <div className="flex items-center justify-center sm:justify-start">
      <Icon className="h-5 w-5 flex-shrink-0 text-green-600 mr-2" />
      <span className="text-sm sm:text-base font-medium text-gray-900 whitespace-nowrap">{text}</span>
    </div>
  </div>
);

const Stat = ({ icon: Icon, text }) => (
  <div className="flex items-center justify-center bg-gray-50 hover:bg-gray-100 p-4 rounded-2xl transition-all duration-300">
    <Icon className="text-green-600 text-2xl mr-3 flex-shrink-0" />
    <span className="text-gray-900 font-medium text-sm sm:text-base">{text}</span>
  </div>
);

const VideoGallery = () => (
  <div className="grid grid-cols-3 gap-4 w-full max-w-[540px] mx-auto">
    <VideoPlayer src="https://feedbackbyaibe-production.s3.us-east-2.amazonaws.com/save1.mp4" />
    <VideoPlayer src="https://feedbackbyaibe-production.s3.us-east-2.amazonaws.com/final_1744279194.mp4" />
    <VideoPlayer src="https://feedbackbyaibe-production.s3.us-east-2.amazonaws.com/final_1744280922.mp4" />
    {/* <div className="space-y-4">

    </div> */}
  </div>
);

const Hero = () => {
  // --- START: Component Constants (Declared ONCE) ---
  const actionCards = [
    {
      id: "plan",
      icon: "📊",
      title: "Validate Idea with a Comprehensive 100+ Page Business Report",
      description:
        "Get utmost clarity with a 100+ page business report  personalized to your idea — use it for strategy, expansion or raising funds.",
      cta: "Create Your Plan",
      path: "/dynamicreports/",
      color: "green",
      glow: "shadow-[0_0_35px_-8px_rgba(34,197,94,0.25)]",
      hoverGlow: "hover:shadow-[0_0_45px_-10px_rgba(34,197,94,0.4)]",
    },
    {
      id: "leads",
      icon: "👥",
      title: "Real-Time Buyer Intent Search",
      description:
        "Discover high intent leads already searching for solutions like yours on Reddit, Twitter, and LinkedIn — all from one dashboard.",
      cta: "Discover Leads",
      isPopular: true,
      path: "/leadfinder",
      color: "purple",
      glow: "shadow-[0_0_35px_-8px_rgba(168,85,247,0.25)]",
      hoverGlow: "hover:shadow-[0_0_45px_-10px_rgba(168,85,247,0.4)]",
    },
    {
      id: "content",
      icon: "📱",
      title: "Launch Your AI Influencer",
      description:
        "Tiktoks and Reels now drive the most product awareness . Automate tiktoks at scale. 50+ Hyperrealistic avatars for your next tiktok. Automate in 3 clicks.",
      // "Your content team — without the overhead. Create daily TikToks, Reels, and Shorts using 50+ AI avatars. No camera, no editing, just scroll-stopping videos that convert.",
      cta: "Generate Videos",
      path: "/video",
      color: "orange",
      glow: "shadow-[0_0_35px_-8px_rgba(249,115,22,0.25)]",
      hoverGlow: "hover:shadow-[0_0_45px_-10px_rgba(249,115,22,0.4)]",
    },
    {
      id: "brainstorm",
      icon: "🧪",
      title: "Brainstorm High-Impact Opportunities",
      description:
        "Find features, offers, and pain points worth solving. Tap into live social conversations to uncover real demand — not guesses.",
      cta: "Start Discovering",
      path: "/ideate",
      isPopular: false,
      color: "blue",
      glow: "shadow-[0_0_35px_-8px_rgba(59,130,246,0.25)]",
      hoverGlow: "hover:shadow-[0_0_45px_-10px_rgba(59,130,246,0.4)]",
    },

    {
      id: "whitelabel",
      icon: "🏢",
      title: "White-Label for Agencies, Consultants & Incubators",
      description: "Offer all 4 tools — business plans, lead gen, TikToks, and research — under your own brand, domain, and pricing.",
      cta: "Become a Partner",
      path: "#white-label", // This will scroll to your WhiteLabelSection
      color: "indigo",
      glow: "shadow-[0_0_35px_-8px_rgba(99,102,241,0.25)]",
      hoverGlow: "hover:shadow-[0_0_45px_-10px_rgba(99,102,241,0.4)]",
    },
  ];

  const colorClasses = {
    blue: {
      bg: "bg-white",
      border: "border-blue-200",
      text: "text-blue-900",
      hoverBg: "hover:bg-blue-50/50",
      hoverBorder: "hover:border-blue-400",
      iconBg: "bg-blue-100",
      iconText: "text-blue-600",
      ctaColor: "text-blue-600",
      hoverCtaColor: "group-hover:text-blue-700",
      gradientFrom: "from-blue-50/50",
      gradientTo: "to-transparent",
      popularRing: "ring-blue-500",
      popularText: "text-blue-700",
      popularBg: "bg-blue-100",
    },
    green: {
      bg: "bg-white",
      border: "border-green-200",
      text: "text-green-900",
      hoverBg: "hover:bg-green-50/50",
      hoverBorder: "hover:border-green-400",
      iconBg: "bg-green-100",
      iconText: "text-green-600",
      ctaColor: "text-green-600",
      hoverCtaColor: "group-hover:text-green-700",
      gradientFrom: "from-green-50/50",
      gradientTo: "to-transparent",
      popularRing: "ring-green-500",
      popularText: "text-green-700",
      popularBg: "bg-green-100",
    },
    purple: {
      bg: "bg-white",
      border: "border-purple-200",
      text: "text-purple-900",
      hoverBg: "hover:bg-purple-50/50",
      hoverBorder: "hover:border-purple-400",
      iconBg: "bg-purple-100",
      iconText: "text-purple-600",
      ctaColor: "text-purple-600",
      hoverCtaColor: "group-hover:text-purple-700",
      gradientFrom: "from-purple-50/50",
      gradientTo: "to-transparent",
      popularRing: "ring-purple-500",
      popularText: "text-purple-700",
      popularBg: "bg-purple-100",
    },
    orange: {
      bg: "bg-white",
      border: "border-orange-200",
      text: "text-orange-900",
      hoverBg: "hover:bg-orange-50/50",
      hoverBorder: "hover:border-orange-400",
      iconBg: "bg-orange-100",
      iconText: "text-orange-600",
      ctaColor: "text-orange-600",
      hoverCtaColor: "group-hover:text-orange-700",
      gradientFrom: "from-orange-50/50",
      gradientTo: "to-transparent",
      popularRing: "ring-orange-500",
      popularText: "text-orange-700",
      popularBg: "bg-orange-100",
    },
    // Add this to your colorClasses object
    indigo: {
      bg: "bg-white",
      border: "border-indigo-200",
      text: "text-indigo-900",
      hoverBg: "hover:bg-indigo-50/50",
      hoverBorder: "hover:border-indigo-400",
      iconBg: "bg-indigo-100",
      iconText: "text-indigo-600",
      ctaColor: "text-indigo-600",
      hoverCtaColor: "group-hover:text-indigo-700",
      gradientFrom: "from-indigo-50/50",
      gradientTo: "to-transparent",
      popularRing: "ring-indigo-500",
      popularText: "text-indigo-700",
      popularBg: "bg-indigo-100",
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { delayChildren: 0.3, staggerChildren: 0.18 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 30 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", damping: 15, stiffness: 100 } },
  };

  const delayedFadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8, delay: 1.2 } },
  };

  // --- END: Component Constants ---

  // --- START: Component Return JSX ---
  return (
    <div className="relative min-h-screen flex flex-col justify-center bg-gradient-to-br from-white via-gray-50 to-blue-50 py-20 sm:py-24 px-4 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-70">
        <div className="absolute top-[-20%] left-[-10%] w-[50vw] h-[50vw] bg-green-100 rounded-full blur-3xl animate-pulse-slow-light"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[45vw] h-[45vw] bg-blue-100 rounded-full blur-3xl animate-pulse-slower-light"></div>
        <div className="absolute top-[30%] right-[5%] w-[30vw] h-[30vw] bg-purple-100 rounded-full blur-3xl animate-pulse-slow-light delay-1000"></div>
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto text-center flex flex-col justify-center flex-grow">
        {/* Headline Section with improved spacing and animations */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, type: "spring", damping: 15 }}
          className="mb-12 md:mb-16 px-4 sm:px-6 lg:px-8"
        >
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 tracking-tight mb-4 leading-tight">
              Everything you need to run business
              <span className="block mt-2 bg-gradient-to-r from-green-500 to-green-700 bg-clip-text text-transparent">Successfully</span>
            </h1>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.03, boxShadow: "0 10px 25px -5px rgba(34, 197, 94, 0.4)" }}
                whileTap={{ scale: 0.97 }}
                onClick={handleSignUp}
                className="px-8 py-3.5 animate-pulse bg-green-500 hover:bg-green-600 text-white font-medium rounded-full shadow-lg transition-all duration-300"
              >
                Get Started For Free
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                whileTap={{ scale: 0.97 }}
                onClick={handleGoogleSignUp}
                className="px-8 py-3.5 bg-white hover:bg-gray-50 text-gray-800 font-medium rounded-full shadow-lg border border-gray-200 flex items-center justify-center transition-all duration-300"
              >
                <FcGoogle className="text-xl mr-2" />
                Sign in with Google
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Action Cards Grid - Improved responsive behavior */}
        <div className="px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 md:gap-6 lg:gap-7"
          >
            {actionCards.map((card) => {
              // Get color definitions for the current card
              const colors = colorClasses[card.color];

              // Basic check to prevent errors if color definition is missing
              if (!colors) {
                console.warn(`Color definition missing for card color: ${card.color}. Skipping card.`);
                return null;
              }

              // Custom handler for white-label card
              const handleCardClick = () => {
                if (card.id === "whitelabel") {
                  // Scroll to the white-label section smoothly
                  document.getElementById("white-label")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                } else if (card.path) {
                  // For other cards, navigate to the path
                  window.location.href = card.path;
                }
              };

              return (
                <motion.div
                  key={card.id}
                  onClick={handleCardClick}
                  variants={itemVariants}
                  whileHover={{
                    scale: 1.03,
                    y: -5,
                    transition: { type: "spring", stiffness: 300, damping: 15 },
                  }}
                  whileTap={{ scale: 0.98 }}
                  className={`
                    group relative flex flex-col text-left ${colors.bg} ${colors.hoverBg} backdrop-blur-sm
                    border ${colors.border} ${colors.hoverBorder}
                    rounded-2xl p-5 sm:p-6 shadow-lg ${card.glow ?? ""} ${card.hoverGlow ?? ""}
                    transition-all duration-300 ease-out cursor-pointer
                    overflow-hidden h-full
                    ${card.isPopular ? `ring-2 ring-offset-2 ring-offset-gray-50 ${colors.popularRing}` : ""}
                  `}
                >
                  {/* Inner Subtle Gradient with improved animation */}
                  <div
                    className={`absolute inset-0 opacity-60 group-hover:opacity-90 transition-opacity duration-500 bg-gradient-to-br ${colors.gradientFrom} ${colors.gradientTo} rounded-2xl`}
                  ></div>

                  {/* Popular Badge with refined styling */}
                  {card.isPopular && (
                    <div
                      className={`absolute top-3 right-3 ${colors.popularBg} text-xs font-bold uppercase tracking-wider ${colors.popularText} px-3 py-1 rounded-full shadow-md z-20`}
                    >
                      Popular
                    </div>
                  )}

                  {/* Card Content Wrapper with improved spacing */}
                  <div className="relative z-10 flex flex-col h-full">
                    {/* Icon with refined shadow and animation */}
                    <div
                      className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center ${colors.iconBg} mb-4 sm:mb-5 shadow-sm flex-shrink-0 group-hover:shadow-md transition-all duration-300`}
                    >
                      <span className="text-2xl">{card.icon}</span>
                    </div>

                    {/* Title with responsive sizing */}
                    <h3 className={`text-lg sm:text-xl font-semibold ${colors.text} mb-2`}>{card.title}</h3>

                    {/* Description with improved readability */}
                    <p className={`text-sm text-gray-700 mb-5 flex-grow leading-relaxed`}>{card.description}</p>

                    {/* CTA with better hover effect */}
                    <div
                      className={`mt-auto flex items-center text-sm sm:text-base font-medium ${colors.ctaColor} ${colors.hoverCtaColor} transition-colors duration-300`}
                    >
                      {card.cta}
                      <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 transform transition-transform duration-300 group-hover:translate-x-1.5" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Added floating elements for visual interest */}
        <div className="absolute top-20 left-0 w-24 h-24 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-40 right-10 w-36 h-36 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/3 right-1/4 w-28 h-28 bg-green-100 rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-blob animation-delay-4000"></div>
      </div>

      {/* Add this to your global styles or component styles */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      {/* Add this to your global styles or component styles */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
      {/* Secondary Content Area (Below the fold usually) */}
      <div className="relative z-10 w-full max-w-6xl mx-auto text-center mt-20 sm:mt-24 lg:mt-32">
        {/* Trust Badge */}
        <motion.div
          variants={delayedFadeIn}
          initial="hidden"
          animate="visible"
          className="inline-flex items-center gap-2 border border-gray-200 bg-white/70 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm text-gray-600"
        >
          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          Trusted by 10,000+ businesses
        </motion.div>

        {/* Testimonials Section */}
        {/* <motion.div variants={delayedFadeIn} initial="hidden" animate="visible" className="mt-12 sm:mt-16 max-w-5xl mx-auto">
          <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <IframeResizer
              src="https://embed-v2.testimonial.to/w/feedbackbyai" // Replace with your actual embed URL
              style={{ minHeight: "450px" }} // Basic style, relies on iframe-resizer defaults mostly
            />
          </div>
        </motion.div> */}

        <motion.div variants={delayedFadeIn} initial="hidden" animate="visible" className="mt-12 sm:mt-16 max-w-5xl mx-auto">
          <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <IframeResizer
              src="https://embed-v2.testimonial.to/w/feedbackbyai"
              style={{ minHeight: "450px", width: "100%" }}
              className="w-full"
              scrolling="no"
              frameBorder="0"
            />
          </div>
        </motion.div>
      </div>

      {/* Global CSS for Animations (Place within component or in global CSS file) */}
      <style jsx global>{`
        @keyframes pulse-slow-light {
          0%,
          100% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 0.9;
            transform: scale(1.05);
          }
        }
        @keyframes pulse-slower-light {
          0%,
          100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.03);
          }
        }
        .animate-pulse-slow-light {
          animation: pulse-slow-light 10s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .animate-pulse-slower-light {
          animation: pulse-slower-light 12s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        /* Add a slight delay to the third pulse animation */
        .delay-1000 {
          animation-delay: 1s; /* 1000ms delay */
        }
      `}</style>
    </div> // End of main container div
  ); // End of return
  // --- END: Component Return JSX ---
}; // End of Hero component function

const VideoFeature = ({ title, description, video, isReversed, features }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = React.useRef(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="py-24">
      <div className="grid md:grid-cols-2 gap-16 xl:gap-20 items-center">
        <div className={isReversed ? "md:order-2" : ""}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-semibold text-gray-900 tracking-tight mb-6">{title}</h2>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed">{description}</p>
            <div className="space-y-5">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="flex items-start"
                >
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-1">
                    <HiOutlineCheck className="h-4 w-4 text-green-600" />
                  </div>
                  <p className="ml-4 text-gray-600 leading-relaxed">{feature}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`relative ${isReversed ? "md:order-1" : ""}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="rounded-2xl overflow-hidden bg-gray-50 shadow-xl transform transition-all duration-500 hover:shadow-2xl">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/20 z-10" />
              <video
                ref={videoRef}
                src={video}
                className="w-full rounded-2xl transform transition-transform duration-700 scale-[1.01] hover:scale-[1.02]"
                loop
                muted
              />
              <div
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
                  isHovered ? "opacity-100" : "opacity-0"
                }`}
              >
                <button
                  onClick={togglePlay}
                  className="relative z-20 bg-white/95 p-5 rounded-full shadow-[0_8px_28px_-6px_rgba(0,0,0,0.3)] transform transition-all duration-500 hover:scale-110 hover:bg-white"
                >
                  <HiPlay className="h-8 w-8 text-green-600" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const Features = () => (
  <section className="max-w-[1200px] mx-auto px-4 sm:px-6" id="features">
    <VideoFeature
      title="Validate Your Idea with a Comprehensive 100+ Page Report"
      description=""
      // video="/videos/businessplan.mp4"
      video="https://feedbackbyaibe-production.s3.us-east-2.amazonaws.com/businessplan.mp4"
      isReversed={true}
      features={[
        "Custom market research",
        "Step-by-step go-to-market strategy",
        "Early risk detection",
        "Competitive moat analysis",
        "Business model clarity",
        "Execution roadmap",
        " 50+ more expert-built sections to guide your launch with confidence.",
      ]}
    />

    <VideoFeature
      title="  Real-Time Buyer Intent Search"
      description="From a single platform, discover and target high-intent leads already looking for solutions like yours — across Reddit, Twitter, and LinkedIn."
      // video="/videos/leadfinder.mp4"
      video="https://feedbackbyaibe-production.s3.us-east-2.amazonaws.com/leadfinder.mp4"
      features={[
        "Track high-intent keywords",
        "Get warm leads from Reddit, Twitter, LinkedIn",
        "Prioritized lead list = instant outreach",
        "Outcome: No more cold DMs. Just customers in buying mode.",
      ]}
    />

    {/* <VideoFeature
      title="3. Interview 50+ AI Personas—Instantly"
      description="Skip scheduling calls."
      // video="/videos/userfeedback.mp4"
      video="https://feedbackbyaibe-production.s3.us-east-2.amazonaws.com/userfeedback.mp4"
      features={[
        "Test features, messaging, and pricing with realistic, industry‑specific personas and sentiment scoring. ",
        "Outcome: Rapid, honest feedback without the wait.",
      ]}
    /> */}

    <VideoFeature
      // title="Generate Viral-Ready Social Content"
      title=" 🎥  Automate TikTok and Reels at Scale"
      // description="Automate TikTok and Reels at Scale"
      // description="Create engaging TikTok & Instagram Reels automatically. Our AI handles scripts, hooks, and video structure to help you grow your audience."
      // video="/videos/drivetraffic.mp4"
      video="https://feedbackbyaibe-production.s3.us-east-2.amazonaws.com/drivetraffic.mp4"
      isReversed={true}
      features={[
        "UGC-style TikToks and Instagram Reels drive most product awareness today",
        "Choose from 50+ hyper-realistic hooks",
        "Upload a product demo",
        "Ready-to-post on TikTok, Instagram, and YouTube",
        "Outcome: Consistent traffic without cameras, editors, or burnout",
      ]}
    />

    <VideoFeature
      title="🧠 Brainstorm Like a Pro"
      description="Tap into realtime social media conversations to find gaps in current market."
      // video="/videos/ideation.mp4"
      video="https://feedbackbyaibe-production.s3.us-east-2.amazonaws.com/ideation.mp4"
      isReversed={true}
      features={[
        "Discover rising pain points & unmet needs",
        "See what your market is talking about — right now",
        "Start with data, not assumptions",
        "Outcome: Build what people actually want.",
      ]}
    />
  </section>
);

// Pricing Section
const PricingTier = ({ name, price, description, features, isPopular }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className={`bg-white rounded-2xl ${isPopular ? "ring-2 ring-green-600 shadow-xl" : "border border-gray-100 shadow-lg"}`}
  >
    <div className="p-8">
      {isPopular && (
        <span className="inline-block bg-green-50 text-green-600 text-sm font-medium px-3 py-1 rounded-full mb-4">Most Popular</span>
      )}
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{name}</h3>
      <div className="mb-6">
        <span className="text-4xl font-bold text-gray-900">${price}</span>
        <span className="text-gray-600">/month</span>
      </div>
      <p className="text-gray-600 mb-8">{description}</p>
      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <HiOutlineCheck className="h-5 w-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
            <span className="text-gray-600">{feature}</span>
          </li>
        ))}
      </ul>
      <button
        className={`w-full py-4 rounded-full text-[17px] font-medium transition-all duration-300 ${
          isPopular ? "bg-green-600 text-white hover:bg-green-700" : "bg-gray-100 text-gray-900 hover:bg-gray-200"
        }`}
        onClick={handleSignUp}
      >
        Get Started
      </button>
    </div>
  </motion.div>
);

const Pricing = () => (
  <section className="py-24" id="pricing">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
      <div className="text-center mb-16">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl font-semibold text-gray-900 mb-4"
        >
          {/* Simple, transparent pricing */}
          Choose Your Growth Plan
        </motion.h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* <PricingTier
          name="Starter"
          price={19}
          description="Perfect for testing your concept"
          features={[
            // "Daily Ideation",
            // "10 AI User Interviews",
            // "10 AI UGC videos/month",
            // "10 leads/month",
            // "No Business Report",
            // "No Community Access (Coming Soon)",

            // "Daily Ideation",
            "20 Idea discovery credits",
            // "10 AI User Interviews",
            "10 AI marketing videos per month",
            "20 Lead discovery credits",
            "Track 3 Keywords (Reddit + Twitter + LinkedIn)",
            "Full access to all 50+ AI avatars",
            "1 Executive Summary Report (Lite Version)",
            "❌ No Full Business Plan",
            "❌ No Export as PDF",

            // "❌ Can't create UGC avatars",
            // "10 leads/month",
            // "❌ No Full Business Report Generation",
            // "❌ No Community Access (Coming Soon)",
          ]}
        /> */}

        <PricingTier
          name="Starter"
          price={59}
          description="FOR TESTING THE WATERS"
          features={[
            // "Daily Ideation",
            // "10 AI User Interviews",
            // "10 AI UGC videos/month",
            // "10 leads/month",
            // "No Business Report",
            // "No Community Access (Coming Soon)",

            // "Daily Ideation",
            "25 Idea discovery credits",
            // "10 AI User Interviews",
            "25 AI marketing videos per month",
            "30 Lead discovery credits",
            "Track 5 Keywords (Reddit + Twitter + LinkedIn)",
            "Full access to all 50+ AI avatars",
            // "1 Executive Summary Report (Lite Version)",
            "2 Full 100+ Page Business Reports",
            "Export Full Report as PDF/Docs",

            // "❌ Can't create UGC avatars",
            // "10 leads/month",
            // "❌ No Full Business Report Generation",
            // "❌ No Community Access (Coming Soon)",
          ]}
        />

        {/* <PricingTier
          name="Select"
          price={39}
          description="Everything needed to validate and launch"
          features={[
            // "Daily Ideation",
            // "50 AI User Interviews",
            // "50 UGC videos/month",
            // "50 leads/month",
            // "1 Full 100+ Page Business Report",
            // "Community Access (coming soon)",
            "25 Idea discovery credits",
            "25 AI marketing videos per month",
            "50 Lead discovery credits",
            "Track 5 Keywords (Reddit + Twitter + LinkedIn)",
            "Full access to all 50+ AI avatars",
            // "Create your own AI avatars (25 videos)",
            // "50 AI User Interviews",
            // "50 leads/month",
            // "Complete 100+ page business report",
            "1 Full 100+ Page Business Report",
            "Export reports as PDF/Docs",
            // "Community Access",
          ]}
        /> */}

        {/* <PricingTier
          name="Select"
          price={59}
          description="Everything needed to validate and launch"
          features={[
            // "Daily Ideation",
            // "50 AI User Interviews",
            // "50 UGC videos/month",
            // "50 leads/month",
            // "1 Full 100+ Page Business Report",
            // "Community Access (coming soon)",
            "50 Idea discovery credits",
            "50 AI marketing videos per month",
            "Access to all 50+ AI avatars",
            // "Create your own AI avatars (25 videos)",
            // "50 AI User Interviews",
            // "50 leads/month",
            // "Complete 100+ page business report",
            "1 Full 100+ Page Business Report",
            "Export reports as PDF/Docs",
            // "Community Access",
          ]}
        /> */}

        <PricingTier
          // name="Pro"
          name="Select"
          price={95}
          isPopular={true}
          description="FOR SERIOUS BUSINESS BUILDERS"
          features={[
            // "Daily Ideation",
            // "100 AI User Interviews",
            // "100 UGC videos/month",
            // "100 leads/month",
            // "2 Full 100+ Page Business Report",
            // "Community Access (coming soon)",
            // "250 Ideation Credits",
            "25 Idea discovery credits",
            "25 AI marketing videos per month",
            "75 Lead discovery credits",
            "Track 7 Keywords (Reddit + Twitter + LinkedIn)",
            "Full access to all 50+ AI avatars",

            // "Create your own AI avatars (100 videos)",
            // "150 AI User Interviews",
            // "100 AI UGC videos/month",

            // "300 leads/month",
            "3 Full 100+ Page Business Reports",
            "Export Full Report as PDF/Docs",

            // "Community Access (Coming Soon)",
          ]}
        />

        <PricingTier
          name="Pro"
          price={149}
          // isPopular={true}
          description="FOR SCALING BUSINESS"
          features={[
            // "Daily Ideation",
            // "100 AI User Interviews",
            // "100 UGC videos/month",
            // "100 leads/month",
            // "2 Full 100+ Page Business Report",
            // "Community Access (coming soon)",
            // "250 Ideation Credits",
            "25 Idea discovery credits",
            "50 AI marketing videos per month",
            "150 Lead discovery credits",
            "Track 10 Keywords (Reddit + Twitter + LinkedIn)",
            "Full access to all 50+ AI avatars",
            // "Create your own AI avatars (100 videos)",
            // "150 AI User Interviews",
            // "100 AI UGC videos/month",

            // "300 leads/month",
            "5 Full 100+ Page Business Reports",
            "Export Full Report as PDF/Docs",
            // "Community Access (Coming Soon)",
          ]}
        />

        {/* <PricingTier
          name="Pro"
          price={248}
          isPopular={true}
          description="For scaling multiple products"
          features={[
            // "Daily Ideation",
            // "100 AI User Interviews",
            // "100 UGC videos/month",
            // "100 leads/month",
            // "2 Full 100+ Page Business Report",
            // "Community Access (coming soon)",
            // "250 Ideation Credits",
            "100 Idea discovery credits",

            "250 AI marketing videos per month",
            "Access to all 50+ AI avatars",
            // "Create your own AI avatars (100 videos)",
            // "150 AI User Interviews",
            // "100 AI UGC videos/month",

            // "300 leads/month",
            "5 Full 100+ Page Business Reports",
            "Export Full Report as PDF/Docs",
            // "Community Access (Coming Soon)",
          ]}
        /> */}
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="border-b border-gray-200 last:border-0"
    >
      <button onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full py-6 text-left">
        <span className="text-lg font-medium text-gray-900">{question}</span>
        <HiChevronDown className={`h-5 w-5 text-gray-600 transform transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 pb-6" : "max-h-0"}`}>
        <p className="text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </motion.div>
  );
};

const FAQ = () => (
  <section className="py-24 bg-gray-50" id="faq">
    <div className="max-w-[800px] mx-auto px-4 sm:px-6">
      <div className="text-center mb-16">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl font-semibold text-gray-900 mb-4"
        >
          Frequently asked questions
        </motion.h2>
      </div>

      <div className="space-y-2">
        <FAQItem
          question="1. How does the AI brainstorm feature actually work?"
          answer="We analyze live conversations across Reddit, Twitter, and LinkedIn using real-time data signals. The AI surfaces rising keywords, unmet needs, and emerging pain points — giving you a list of product ideas or feature expansions backed by actual demand, not guesses."
        />

        <FAQItem
          question="2. What’s included in the 100+ page business plan?"
          answer="Everything investors want to see: market analysis, competitive landscape, customer personas, pricing strategy, go-to-market plan, financial projections, and a detailed launch roadmap tailored to your specific business concept."
        />

        {/* <FAQItem
          question="How realistic are the AI user interviews?"
          answer="Our 51+ AI personas are crafted based on real demographic data, consumer behavior patterns, and market research. Each persona has unique characteristics, preferences, and decision-making patterns, providing diverse perspectives that closely mirror real customer feedback."
        /> */}

        <FAQItem
          question="3. Who is this platform for — new startups or existing businesses?"
          answer="Both. New founders use it to validate ideas and build investor-ready plans.Existing businesses use it to discover new opportunities, automate content, and find ready-to-buy leads."
        />

        <FAQItem
          question="4. Do I need to be on camera for TikToks or Reels?"
          answer="Nope. Just pick from 50+ AI avatars and upload product demo — no filming, no editing."
        />

        <FAQItem
          question="5. What is Buyer Intent Search, and how is it different from normal lead tools?"
          answer="Buyer Intent Search doesn’t scrape random profiles. It detects live signals from people searching for help, solutions, or recommendations — based on conversations happening now across platforms.
You get a ranked lead list of people actually in “buy mode.”"
        />

        <FAQItem
          question="6. Do I need technical skills to use this platform?"
          answer="Nope. Everything is no-code and beginner-friendly.
You can generate videos, leads, and plans in a few clicks — even if you’ve never used an AI tool before."
        />

        <FAQItem
          question="7. Can I edit or customize videos and reports?"
          answer="Yes — you can remix hooks, swap avatars, change captions, and edit business plans in Google Docs or PDF before sharing."
        />

        <FAQItem
          question="8. Can I upgrade or downgrade my plan anytime?"
          answer="Yes. You can change, upgrade, or cancel your plan anytime from your dashboard. No long-term contracts, no hassle."
        />

        <FAQItem
          question="9. Will this actually help me grow my business?"
          answer="Absolutely — if you use it.
You’ll save time on research, content, and outreach — and focus only on actions that drive leads, clarity, and visibility. It’s built to help you move fast and make smarter decisions with data."
        />

        {/*
        <FAQItem
          question="How does the lead generation feature find potential customers?"
          answer="The AI scans public social media posts and comments for keywords indicating purchase intent or problems your solution addresses. It then qualifies these potential leads based on relevance."
        /> */}

        <FAQItem
          question="10. Do you offer white-label or agency solutions?"
          answer="Yes! We offer a white-label version of FeedbackbyAI designed for consultants, agencies, accelerators, and incubators. You can deliver investor-ready business plans, lead generation, and AI-powered marketing — all under your own branding and domain. Just use the form above or email us at business@feedbackbyai.com"
        />

        <FAQItem
          question="11. What if I want help or support?"
          answer="Our team is available 24/7 via chat or email at business@feedbackbyai.com. Premium users receive priority response within 2 hours."
        />
      </div>
    </div>
  </section>
);

// Final CTA Section
const FinalCTA = () => (
  <section className="py-12 bg-green-600">
    <div className="max-w-[800px] mx-auto px-4 sm:px-6 text-center">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-4xl font-bold text-white mb-6"
      >
        {/* Ready to Turn Ideas into Income? */}
        {/* Ready to Validate Your Idea? */}
        {/* Ready to Scale Your Business? */}
        🚀 Ready to Launch Your Growth Engine?
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        className="text-xl text-green-50 mb-8"
      >
        {/* Stop wasting time on ideas that won't succeed. FeedbackbyAI gives you the validation, plan, and marketing tools to build a business
        that actually makes money. */}
        {/* Unlock powerful AI tools to brainstorm ideas, build a winning plan, find high-intent leads, and drive traffic—faster than ever. */}
        {/* Get the only platform that helps you brainstorm smarter, validate faster, find real buyers, and post daily — without showing up on
        camera. */}
        Get the only platform that helps you brainstorm smarter, validate faster, find real buyers, and post daily — without showing up on
        camera.
        {/* Validate Your Idea  */}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="flex flex-col sm:flex-row justify-center gap-4 mb-8"
      >
        <button
          className="px-8 py-4 text-[17px] font-medium bg-white text-green-600 rounded-full hover:bg-green-50 transition-all duration-300"
          onClick={handleSignUp}
        >
          Start for Free
        </button>
        <button
          className="flex items-center justify-center px-8 py-4 text-[17px] font-medium bg-green-700 text-white rounded-full hover:bg-green-800 transition-all duration-300"
          onClick={handleGoogleSignUp}
        >
          <FcGoogle className="text-xl mr-2" />
          Sign in with Google
        </button>
      </motion.div>
    </div>
  </section>
);

const ToolkitIntro = () => {
  const tools = [
    {
      icon: HiChartBar,
      title: "AI Business Plan Generator",
      description: "Comprehensive validation and strategy",
    },
    {
      icon: HiChatAlt2,
      title: "AI User Interviews",
      description: "Instant feedback from 50+ personas",
    },
    {
      icon: HiBeaker,
      title: "Market Opportunity Finder",
      description: "Discover untapped market gaps",
    },
    {
      icon: HiLightningBolt,
      title: "Social Video Generator",
      description: "Automated content for growth",
    },
    {
      icon: HiSparkles,
      title: "Lead Generation AI",
      description: "Connect with ready buyers",
    },
  ];

  return (
    <section className="relative py-0 bg-white">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-gray-900 mb-6">What You Can Do With FeedbackbyAI</h2>
          {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Harness these powerful tools, seamlessly integrated to take you from zero to launch and beyond:
          </p> */}
        </motion.div>
      </div>
    </section>
  );
};

// Main Landing Page Component
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* <Header /> */}
      <EnhancedHeader />
      <Hero handleSignUp={handleSignUp} handleGoogleSignUp={handleGoogleSignUp} />
      {/* <ProblemSection handleSignUp={handleSignUp} /> */}
      {/* <HowItWorks /> */}
      <ToolkitIntro />
      <Features />
      <WhiteLabelSection />

      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
